import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import * as z from 'zod'

import { customInstance, isAuthRefreshFailedError } from '@/api/client'
import { type CouponPlan, PLAN_INTERVALS } from '@/domain/coupons'

// TODO(MKT-745): temporary shim — replace with the orval-generated hook once
// GET /v4/affiliates/admin/subscription-plans/ is on dev. The affiliates-web
// orval client does not yet generate it (a separate backend PR adds the
// endpoint). Mirrors the custom-hook pattern in pages/admin/settlements/api.ts.
// The coupon plan-scope selector uses this to resolve a chosen scope into
// concrete plan_ids.
//
// Repointed off /v4/payments/subscription-plan/, which the affiliate-admin
// token is not authorized for (401 at runtime); the new admin endpoint returns
// the SAME bare-array shape parsed below.

// The admin subscription-plan response shape we consume (id, interval, tier,
// type, variant, version) — the fields coupon plan-scoping needs. Runtime-
// validated (queryFn below) rather than trusted, since orval doesn't generate
// this endpoint yet. `interval` reuses the domain's `PLAN_INTERVALS` so the wire
// enum can't drift from the taxonomy the scope resolver understands.
const subscriptionPlanWireSchema = z.object({
  id: z.number(),
  interval: z.enum(PLAN_INTERVALS),
  tier: z.string(),
  type: z.string(),
  variant: z.string().nullish(),
  version: z.number(),
})

/** The pre-`select` cache shape. Exported so a test seeding `subscriptionPlanKeys`
 *  is held to the same contract `queryFn` parses, rather than an unchecked
 *  `setQueryData<unknown>` literal that can drift as this schema grows. */
export type SubscriptionPlanWire = z.infer<typeof subscriptionPlanWireSchema>

export const subscriptionPlanKeys = {
  all: ['subscription-plans'] as const,
}

const toCouponPlan = (wire: SubscriptionPlanWire): CouponPlan => ({
  id: wire.id,
  interval: wire.interval,
  tier: wire.tier,
  type: wire.type,
  variant: wire.variant ?? null,
  version: wire.version,
})

/**
 * The subscription-plan catalog as `CouponPlan[]` (raw — eligibility filtering
 * and version dedup live in the domain layer). `enabled` lets a caller defer
 * the fetch until the create/edit dialog is actually opened.
 */
export const useSubscriptionPlans = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: subscriptionPlanKeys.all,
    queryFn: async () => {
      const data = await customInstance<unknown>({
        url: '/v4/affiliates/admin/subscription-plans/',
        method: 'GET',
      })

      // Validate the hand-authored boundary at runtime instead of trusting a
      // generic. Keep only well-formed plans — a malformed or unknown-interval
      // element is dropped here (the domain eligibility filter would drop it
      // anyway), so one bad row can't blank the whole catalog. A non-array body
      // fails `z.array` and surfaces via the query's error state.
      return z
        .array(z.unknown())
        .parse(data)
        .flatMap((element) => {
          const parsed = subscriptionPlanWireSchema.safeParse(element)

          return parsed.success ? [parsed.data] : []
        })
    },
    select: (data) => data.map(toCouponPlan),
    enabled,
    // The catalog is a soft dependency — the scope selector degrades to "All
    // plans" on failure — so fail fast rather than sit pending: never retry an
    // auth failure or any 4xx (they won't self-heal), and cap transient
    // (5xx/network) retries. This lets a failure resolve to `isError` promptly
    // so the selector's warning surfaces instead of hanging.
    retry: (failureCount, error: unknown) => {
      if (isAuthRefreshFailedError(error)) {
        return false
      }
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status !== undefined && status >= 400 && status < 500) {
        return false
      }

      return failureCount < 2
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  })
